import { AppWindow, ChevronDown, ChevronRight, Globe, Search } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  useAppsReport,
  useSaveCatalogEntry,
  type AppCategory,
  type AppsReportItem,
  type AppsScope,
} from "@/api/queries";
import { useCurrentUser } from "@/app/auth";
import { CategoryBar, CategoryLabel } from "@/components/common/Category";
import { CopyButton } from "@/components/common/CopyButton";
import { EmptyState } from "@/components/common/EmptyState";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input, NativeSelect } from "@/components/ui/input";
import { Segmented } from "@/components/ui/segmented";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { categories } from "@/lib/categories";
import { errorText } from "@/lib/errors";
import { dateInZone, formatDuration, percentOf } from "@/lib/format";
import { validPeriod } from "@/lib/periods";
import { cn } from "@/lib/utils";

type Kind = "all" | "app" | "url";

type AppsReportViewProps = {
  scope: AppsScope;
  /** The report may cover several people: show the people column. */
  multiPeople: boolean;
  /** Controls placed before the type switch and the search. */
  toolbar?: ReactNode;
};

/** Applications and sites of a period with categories (Activity and Reports). */
export function AppsReportView({ scope, multiPeople, toolbar }: AppsReportViewProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "ru";
  const me = useCurrentUser();
  const [kind, setKind] = useState<Kind>("all");
  const [query, setQuery] = useState("");
  const today = dateInZone(new Date(), me.timezone);
  const invalidRange = !validPeriod(scope.from, scope.to);
  const report = useAppsReport(invalidRange ? { ...scope, from: today, to: today } : scope);

  const data = report.data;
  const q = query.trim().toLowerCase();
  const items = (data?.items ?? []).filter(
    (a) =>
      (kind === "all" || a.kind === kind) &&
      (!q || a.name.toLowerCase().includes(q) || a.key.toLowerCase().includes(q)),
  );
  const multi = (data?.users ?? 0) > 1 || multiPeople;

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {toolbar}
        <Segmented
          aria-label={t("categories.title")}
          value={kind}
          onChange={setKind}
          options={(["all", "app", "url"] as const).map((k) => ({ value: k, label: t(`apps.kinds.${k}`) }))}
        />
        <div className="relative ml-auto w-full sm:w-64">
          <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted" aria-hidden="true" />
          <Input
            type="search"
            aria-label={t("apps.search")}
            placeholder={t("apps.search")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-9 pl-9"
          />
        </div>
      </div>

      {invalidRange ? (
        <Alert variant="warning">{t("apps.periodTooLong")}</Alert>
      ) : report.isError ? (
        <Card>
          <EmptyState
            icon={AppWindow}
            title={errorText(t, report.error)}
            action={
              <Button variant="secondary" onClick={() => void report.refetch()}>
                {t("common.retry")}
              </Button>
            }
          />
        </Card>
      ) : !data ? (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-72 w-full" />
        </div>
      ) : data.items.length === 0 ? (
        <Card>
          <EmptyState icon={AppWindow} title={t("apps.empty")} description={t("apps.emptyDescription")} />
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          <Card className="flex flex-col gap-3 p-5">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-xl font-semibold tabular-nums">
                {t("apps.total", { time: formatDuration(data.total_seconds, lang) })}
              </span>
              {multi && <span className="text-muted">{t("apps.people", { count: data.users })}</span>}
            </div>
            <CategoryBar
              lang={lang}
              totals={{
                productive: data.categories.productive_seconds,
                neutral: data.categories.neutral_seconds,
                unproductive: data.categories.unproductive_seconds,
              }}
            />
          </Card>
          <Card className="overflow-hidden">
            <Table>
              <THead>
                <tr>
                  <TH className="w-8" />
                  <TH>{t("apps.columns.name")}</TH>
                  <TH>{t("apps.columns.category")}</TH>
                  <TH className="text-right">{t("apps.columns.time")}</TH>
                  <TH className="w-48">{t("apps.columns.share")}</TH>
                  {multi && <TH className="text-right">{t("apps.columns.users")}</TH>}
                </tr>
              </THead>
              <tbody>
                {items.length === 0 ? (
                  <TR>
                    <TD colSpan={6} className="py-8 text-center text-muted">
                      {t("common.noResults")}
                    </TD>
                  </TR>
                ) : (
                  items.map((a) => (
                    <AppRow
                      key={`${a.kind}:${a.key}`}
                      item={a}
                      total={data.total_seconds}
                      max={data.items[0]?.seconds ?? 1}
                      multi={multi}
                      lang={lang}
                      editable={me.role === "admin"}
                    />
                  ))
                )}
              </tbody>
            </Table>
            {data.items.length >= 200 && (
              <p className="border-t px-5 py-3 text-xs text-muted">{t("apps.limited")}</p>
            )}
          </Card>
        </div>
      )}
    </>
  );
}

type AppRowProps = {
  item: AppsReportItem;
  total: number;
  max: number;
  multi: boolean;
  lang: string;
  editable: boolean;
};

function AppRow({ item: a, total, max, multi, lang, editable }: AppRowProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const save = useSaveCatalogEntry();
  const Icon = a.kind === "url" ? Globe : AppWindow;
  const hasDetails = a.details.length > 0;

  const setCategory = (category: AppCategory) =>
    save.mutate(
      a.catalog_id
        ? { entryId: a.catalog_id, display_name: a.name, category }
        : { kind: a.kind, match_value: a.key, display_name: a.name, category },
    );

  return (
    <>
      <TR>
        <TD className="pr-0">
          {hasDetails && (
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              aria-expanded={open}
              aria-label={open ? t("apps.hideDetails") : t("apps.showDetails")}
              onClick={() => setOpen(!open)}
            >
              {open ? <ChevronDown /> : <ChevronRight />}
            </Button>
          )}
        </TD>
        <TD>
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-surface-muted text-muted">
              <Icon className="size-3.5" aria-hidden="true" />
            </span>
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-medium">{a.name}</span>
              <span className="truncate text-xs text-muted" title={a.key}>
                {a.key !== a.name ? a.key : ""}
                {!a.catalog_id && (
                  <span className="italic">
                    {a.key !== a.name ? " · " : ""}
                    {t("apps.notInCatalog")}
                  </span>
                )}
              </span>
            </div>
          </div>
        </TD>
        <TD>
          {editable ? (
            <NativeSelect
              aria-label={t("apps.categoryFor", { name: a.name })}
              title={t("apps.categoryHint")}
              value={a.category}
              disabled={save.isPending}
              onChange={(e) => setCategory(e.target.value as AppCategory)}
              className={cn(
                "h-8 text-xs",
                a.category === "productive" && "text-success",
                a.category === "unproductive" && "text-danger",
              )}
            >
              {categories.map((c) => (
                <option key={c} value={c}>
                  {t(`categories.${c}`)}
                </option>
              ))}
            </NativeSelect>
          ) : (
            <CategoryLabel category={a.category} />
          )}
          {save.isError && <div className="mt-1 text-xs text-danger">{errorText(t, save.error)}</div>}
        </TD>
        <TD className="text-right font-medium whitespace-nowrap tabular-nums">
          {formatDuration(a.seconds, lang)}
        </TD>
        <TD>
          <div className="flex items-center gap-2">
            <div className="h-1.5 flex-1 rounded-full bg-surface-muted">
              <div
                className="h-1.5 rounded-full bg-primary-solid"
                style={{ width: `${Math.max(2, (a.seconds / max) * 100)}%` }}
              />
            </div>
            <span className="w-9 text-right text-xs text-muted tabular-nums">
              {percentOf(a.seconds, total)}%
            </span>
          </div>
        </TD>
        {multi && <TD className="text-right text-muted tabular-nums">{a.users}</TD>}
      </TR>
      {open && (
        <tr className="bg-surface-muted/40">
          <td />
          <td colSpan={multi ? 5 : 4} className="px-5 pb-3">
            <div className="mb-1 text-xs text-muted">{t(`apps.details.${a.kind}`)}</div>
            <ul className="flex flex-col gap-1">
              {a.details.map((d) => (
                <li key={d.text} className="flex min-w-0 items-center gap-2 text-xs">
                  <span className="min-w-0 flex-1 truncate" title={d.text}>
                    {d.text}
                  </span>
                  {a.kind === "url" && <CopyButton value={d.text} label={t("timeline.copyUrl")} />}
                  <span className="shrink-0 text-muted tabular-nums">{formatDuration(d.seconds, lang)}</span>
                </li>
              ))}
            </ul>
          </td>
        </tr>
      )}
    </>
  );
}
